'use client';

import React, { useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { 
  Button,
  Card, 
  CardContent,
  Input,
  FormField,
} from '../../../components/ui/design-system';
import { AuthService } from '@repo/shared';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Animation variants
const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

const slideUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const schema = z.object({
  email: z.string().email('Invalid email address'),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const handleReset = async (data: FormData) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await AuthService.resetPassword(data.email);
      if (result.error) {
        throw new Error(result.error);
      }
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center mb-8"
        >
          <div className="mx-auto flex items-center justify-center w-12 h-12 premium-premium-radius-base-full bg-green-100 dark:bg-green-900/30">
            <svg
              className="h-6 w-6 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mt-4 mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-premium-700 dark:from-primary-400 dark:to-premium-500">
            Check your email
          </h1>
          <div className="w-16 h-1 mx-auto my-4 bg-gradient-to-r from-primary-500/50 to-premium-700/50 premium-premium-radius-base-full"></div>
          <p className="text-gray-600 dark:text-gray-300">
            We&apos;ve sent you a link to reset your password. Please check your email inbox.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={slideUp}
          className="mt-8 text-center"
        >
          <Link
            href="/auth/login"
            className="text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
          >
            Back to login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-purple-500 dark:from-red-400 dark:to-purple-400">
          Reset your password
        </h1>
        <div className="w-16 h-1 mx-auto my-4 bg-gradient-to-r from-primary-500/50 to-premium-700/50 premium-premium-radius-base-full"></div>
        <p className="text-gray-600 dark:text-gray-300">
          Enter your email address and we&apos;ll send you a link to reset your password
        </p>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={slideUp}
      >
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-100 dark:border-gray-700/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
          <CardContent className="p-6 space-y-6">
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 premium-premium-radius-base-md">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(handleReset)} className="space-y-6">
              <FormField label="Email address" required>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  className="shadow-sm"
                  disabled={isLoading}
                  {...register('email')}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                )}
              </FormField>

              <Button
                type="submit"
                disabled={isLoading}
                variant="primary"
                className="w-full shadow-md hover:shadow-lg"
              >
                {isLoading ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>

            <div className="text-center space-y-2">
              <p className="text-sm">
                Remember your password?{' '}
                <Link href="/auth/login" className="font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                  Back to login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
} 